import api from '../api/client';
// pages/RestockPage.js
import React, { useState, useEffect } from 'react';
import {
    Card,
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    DatePicker,
    Row,
    Col,
    Space,
    Tag,
    message,
    Divider,
    Breadcrumb,
    Statistic,
    Alert,
    Descriptions,
    Modal,
    Table,
    Tabs,
    List,
    Typography
} from 'antd';
import {
    ArrowLeftOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    ShoppingOutlined,
    HomeOutlined,
    CalculatorOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
    SearchOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import moment from 'moment';
import ProTable from '@ant-design/pro-table';

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

const RestockPage = () => {
    // const navigate = useNavigate();
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const [items, setItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [stakeholderInfo, setStakeholderInfo] = useState(null);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [quickAddModalVisible, setQuickAddModalVisible] = useState(false);
    const [selectedQuickItems, setSelectedQuickItems] = useState([]);

    // Product types with categories and common items
    const productTypes = {
        feed: {
            label: 'Feed',
            color: 'green',
            categories: [
                { value: 'ready', label: 'Ready Feed' },
                { value: 'raw', label: 'Raw Material' },
                { value: 'concentrate', label: 'Concentrate' },
                { value: 'supplement', label: 'Feed Supplement' }
            ],
            commonItems: [
                { name: 'Layers Mash', category: 'ready', unit: 'kg', typicalPrice: 45 },
                { name: 'Broiler Starter', category: 'ready', unit: 'kg', typicalPrice: 50 },
                { name: 'Chick Mash', category: 'ready', unit: 'kg', typicalPrice: 48 },
                { name: 'Maize Germ', category: 'raw', unit: 'kg', typicalPrice: 25 },
                { name: 'Wheat Bran', category: 'raw', unit: 'kg', typicalPrice: 20 },
                { name: 'Soya Cake', category: 'concentrate', unit: 'kg', typicalPrice: 65 },
                { name: 'Fish Meal', category: 'concentrate', unit: 'kg', typicalPrice: 120 }
            ]
        },
        vaccine: {
            label: 'Vaccine',
            color: 'red',
            categories: [
                { value: 'poultry', label: 'Poultry Vaccine' },
                { value: 'livestock', label: 'Livestock Vaccine' },
                { value: 'general', label: 'General Vaccine' }
            ],
            commonItems: [
                { name: 'Newcastle Vaccine', category: 'poultry', unit: 'doses', typicalPrice: 5 },
                { name: 'Gumboro Vaccine', category: 'poultry', unit: 'doses', typicalPrice: 4 },
                { name: 'Fowl Pox Vaccine', category: 'poultry', unit: 'doses', typicalPrice: 6 },
                { name: 'Foot and Mouth Vaccine', category: 'livestock', unit: 'doses', typicalPrice: 15 },
                { name: 'Rabies Vaccine', category: 'general', unit: 'doses', typicalPrice: 12 }
            ]
        },
        supplement: {
            label: 'Supplement',
            color: 'blue',
            categories: [
                { value: 'vitamin', label: 'Vitamin' },
                { value: 'mineral', label: 'Mineral' },
                { value: 'probiotic', label: 'Probiotic' },
                { value: 'antibiotic', label: 'Antibiotic' }
            ],
            commonItems: [
                { name: 'Vitamin A Supplement', category: 'vitamin', unit: 'kg', typicalPrice: 200 },
                { name: 'Calcium Powder', category: 'mineral', unit: 'kg', typicalPrice: 150 },
                { name: 'Probiotic Mix', category: 'probiotic', unit: 'kg', typicalPrice: 300 },
                { name: 'Antibiotic Powder', category: 'antibiotic', unit: 'kg', typicalPrice: 250 }
            ]
        },
        food: {
            label: 'Food',
            color: 'orange',
            categories: [
                { value: 'grains', label: 'Grains' },
                { value: 'fruits', label: 'Fruits' },
                { value: 'vegetables', label: 'Vegetables' },
                { value: 'processed', label: 'Processed Food' }
            ],
            commonItems: [
                { name: 'Maize Grain', category: 'grains', unit: 'kg', typicalPrice: 30 },
                { name: 'Wheat Grain', category: 'grains', unit: 'kg', typicalPrice: 35 },
                { name: 'Bananas', category: 'fruits', unit: 'kg', typicalPrice: 40 },
                { name: 'Carrots', category: 'vegetables', unit: 'kg', typicalPrice: 25 }
            ]
        },
        water: {
            label: 'Water',
            color: 'cyan',
            categories: [
                { value: 'drinking', label: 'Drinking Water' },
                { value: 'mineral', label: 'Mineral Water' },
                { value: 'treated', label: 'Treated Water' }
            ],
            commonItems: [
                { name: 'Drinking Water', category: 'drinking', unit: 'liters', typicalPrice: 1 },
                { name: 'Mineral Water', category: 'mineral', unit: 'liters', typicalPrice: 2 },
                { name: 'Treated Water', category: 'treated', unit: 'liters', typicalPrice: 1.5 }
            ]
        }
    };

    // Units by product type
    const unitsByType = {
        feed: ['kg', 'g', 'bags', 'tons'],
        vaccine: ['doses', 'bottles', 'vials', 'packets'],
        supplement: ['kg', 'g', 'tablets', 'capsules', 'bottles'],
        food: ['kg', 'g', 'pieces', 'boxes', 'crates'],
        water: ['liters', 'ml', 'bottles', 'gallons']
    };

    // Payment methods
    const paymentMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'mpesa', label: 'M-Pesa' },
        { value: 'bank', label: 'Bank Transfer' },
        { value: 'credit', label: 'Credit' }
    ];

    // Fetch low stock items
    const fetchLowStockItems = async () => {
        try {
            const response = await fetch('/api/stock/current?lowStock=true');
            const result = await response.json();

            if (result.success) {
                setLowStockItems(result.data);
            }
        } catch (error) {
            console.error('Error fetching low stock items:', error);
        }
    };

    // Fetch stakeholder info
    const fetchStakeholderInfo = async (stakeholderId) => {
        try {
            // Mock data for demonstration
            if (stakeholderId) {
                setStakeholderInfo({
                    id: stakeholderId,
                    name: `Supplier ${stakeholderId}`,
                    type: 'Supplier',
                    contact: '+254 700 000 000',
                    address: 'Nairobi, Kenya'
                });
            }
        } catch (error) {
            console.error('Error fetching stakeholder info:', error);
        }
    };

    // Calculate total amount
    useEffect(() => {
        const total = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
        setTotalAmount(total);
        form.setFieldsValue({ totalAmount: total });
    }, [items, form]);

    // Handle stakeholder ID change
    const handleStakeholderChange = (value) => {
        if (value) {
            fetchStakeholderInfo(value);
        } else {
            setStakeholderInfo(null);
        }
    };

    // Add new item
    const handleAddItem = () => {
        const newItem = {
            id: Date.now(),
            productType: 'feed',
            productCategory: 'ready',
            productName: '',
            brand: '',
            batchNumber: '',
            quantity: 1,
            unit: 'kg',
            unitPrice: 0,
            totalPrice: 0,
            storageLocation: 'Main Store'
        };
        setItems([...items, newItem]);
    };

    // Update item
    const handleUpdateItem = (id, field, value) => {
        setItems(
            items.map((item) => {
                if (item.id === id) {
                    const updatedItem = { ...item, [field]: value };

                    // Auto-calculate total price if quantity or unit price changes
                    if (field === 'quantity' || field === 'unitPrice') {
                        updatedItem.totalPrice = (updatedItem.quantity || 0) * (updatedItem.unitPrice || 0);
                    }

                    return updatedItem;
                }
                return item;
            })
        );
    };

    // Delete item
    const handleDeleteItem = (id) => {
        setItems(items.filter((item) => item.id !== id));
    };

    // Get categories for product type
    const getCategories = (productType) => {
        return productTypes[productType]?.categories || [];
    };

    // Get units for product type
    const getUnits = (productType) => {
        return unitsByType[productType] || ['units'];
    };

    // Get common items for product type
    const getCommonItems = (productType) => {
        return productTypes[productType]?.commonItems || [];
    };

    // Quick add from common items
    const handleQuickAdd = (commonItem, productType) => {
        const newItem = {
            id: Date.now(),
            productType: productType,
            productCategory: commonItem.category,
            productName: commonItem.name,
            brand: '',
            batchNumber: '',
            quantity: 1,
            unit: commonItem.unit,
            unitPrice: commonItem.typicalPrice,
            totalPrice: commonItem.typicalPrice,
            storageLocation: 'Main Store'
        };
        setItems([...items, newItem]);
        message.success(`${commonItem.name} added to restock list`);
    };

    // Add low stock item to restock
    const handleAddLowStockItem = (lowStockItem) => {
        const existingItem = items.find((item) => item.productName === lowStockItem.productName && item.brand === lowStockItem.brand);

        if (existingItem) {
            message.info(`${lowStockItem.productName} is already in the restock list`);
            return;
        }

        const newItem = {
            id: Date.now(),
            productType: lowStockItem.productType,
            productCategory: lowStockItem.productCategory,
            productName: lowStockItem.productName,
            brand: lowStockItem.brand || '',
            batchNumber: lowStockItem.batchNumber || '',
            quantity: Math.max(lowStockItem.minimumStockLevel - lowStockItem.currentQuantity, 1),
            unit: lowStockItem.unit,
            unitPrice: lowStockItem.unitPrice || 0,
            totalPrice: 0,
            storageLocation: lowStockItem.storageLocation || 'Main Store'
        };

        // Calculate total price
        newItem.totalPrice = newItem.quantity * newItem.unitPrice;

        setItems([...items, newItem]);
        message.success(`${lowStockItem.productName} added to restock list`);
    };

    // Add all low stock items
    const handleAddAllLowStock = () => {
        const newItems = lowStockItems
            .filter(
                (lowStockItem) => !items.find((item) => item.productName === lowStockItem.productName && item.brand === lowStockItem.brand)
            )
            .map((lowStockItem) => ({
                id: Date.now() + Math.random(),
                productType: lowStockItem.productType,
                productCategory: lowStockItem.productCategory,
                productName: lowStockItem.productName,
                brand: lowStockItem.brand || '',
                batchNumber: lowStockItem.batchNumber || '',
                quantity: Math.max(lowStockItem.minimumStockLevel - lowStockItem.currentQuantity, 1),
                unit: lowStockItem.unit,
                unitPrice: lowStockItem.unitPrice || 0,
                totalPrice: 0,
                storageLocation: lowStockItem.storageLocation || 'Main Store'
            }))
            .map((item) => ({
                ...item,
                totalPrice: item.quantity * item.unitPrice
            }));

        if (newItems.length === 0) {
            message.info('All low stock items are already in the restock list');
            return;
        }

        setItems([...items, ...newItems]);
        message.success(`${newItems.length} low stock items added to restock list`);
    };

    // Save restock transaction
    const handleSaveRestock = async (values) => {
        if (items.length === 0) {
            message.error('Please add at least one item to restock');
            return;
        }

        setSaving(true);
        try {
            const transactionData = {
                ...values,
                transactionType: 'stock_in',
                transactionDate: values.transactionDate.format('YYYY-MM-DD'),
                recordedBy: 'Admin', // This should come from user context
                userId: 1, // This should come from user context
                userName: 'Admin', // This should come from user context
                totalAmount: totalAmount,
                items: items.map((item) => ({
                    ...item,
                    // Remove temporary ID for backend
                    id: undefined
                }))
            };

            const response = await fetch('/api/stock/transactions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(transactionData)
            });

            const result = await response.json();

            if (result.success) {
                message.success('Restock transaction created successfully');
                // navigate('/stock');
            } else {
                throw new Error(result.message);
            }
        } catch (error) {
            console.error('Error creating restock transaction:', error);
            message.error('Failed to create restock transaction');
        } finally {
            setSaving(false);
        }
    };

    // Go back to stock management
    const handleGoBack = () => {
        // navigate('/stock');
    };

    // Load low stock items on component mount
    useEffect(() => {
        fetchLowStockItems();
    }, []);

    // Columns for ProTable
    const columns = [
        {
            title: 'Product Type',
            dataIndex: 'productType',
            key: 'productType',
            width: 120,
            render: (_, record) => (
                <Select
                    value={record.productType}
                    onChange={(value) => handleUpdateItem(record.id, 'productType', value)}
                    style={{ width: '100%' }}
                >
                    {Object.entries(productTypes).map(([key, type]) => (
                        <Option key={key} value={key}>
                            <Tag color={type.color}>{type.label}</Tag>
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Category',
            dataIndex: 'productCategory',
            key: 'productCategory',
            width: 120,
            render: (_, record) => (
                <Select
                    value={record.productCategory}
                    onChange={(value) => handleUpdateItem(record.id, 'productCategory', value)}
                    style={{ width: '100%' }}
                >
                    {getCategories(record.productType).map((category) => (
                        <Option key={category.value} value={category.value}>
                            {category.label}
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Product Name',
            dataIndex: 'productName',
            key: 'productName',
            width: 150,
            render: (_, record) => (
                <Input
                    value={record.productName}
                    onChange={(e) => handleUpdateItem(record.id, 'productName', e.target.value)}
                    placeholder="Enter product name"
                />
            )
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: 120,
            render: (_, record) => (
                <Input value={record.brand} onChange={(e) => handleUpdateItem(record.id, 'brand', e.target.value)} placeholder="Brand" />
            )
        },
        {
            title: 'Batch No.',
            dataIndex: 'batchNumber',
            key: 'batchNumber',
            width: 120,
            render: (_, record) => (
                <Input
                    value={record.batchNumber}
                    onChange={(e) => handleUpdateItem(record.id, 'batchNumber', e.target.value)}
                    placeholder="Batch number"
                />
            )
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            render: (_, record) => (
                <InputNumber
                    value={record.quantity}
                    onChange={(value) => handleUpdateItem(record.id, 'quantity', value)}
                    min={0.01}
                    step={0.01}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Unit',
            dataIndex: 'unit',
            key: 'unit',
            width: 100,
            render: (_, record) => (
                <Select value={record.unit} onChange={(value) => handleUpdateItem(record.id, 'unit', value)} style={{ width: '100%' }}>
                    {getUnits(record.productType).map((unit) => (
                        <Option key={unit} value={unit}>
                            {unit}
                        </Option>
                    ))}
                </Select>
            )
        },
        {
            title: 'Unit Price (KES)',
            dataIndex: 'unitPrice',
            key: 'unitPrice',
            width: 120,
            render: (_, record) => (
                <InputNumber
                    value={record.unitPrice}
                    onChange={(value) => handleUpdateItem(record.id, 'unitPrice', value)}
                    min={0}
                    step={0.01}
                    formatter={(value) => `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/KES\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Total Price (KES)',
            dataIndex: 'totalPrice',
            key: 'totalPrice',
            width: 120,
            render: (_, record) => (
                <InputNumber
                    value={record.totalPrice}
                    formatter={(value) => `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={(value) => value.replace(/KES\s?|(,*)/g, '')}
                    style={{ width: '100%' }}
                    disabled
                />
            )
        },
        {
            title: 'Storage Location',
            dataIndex: 'storageLocation',
            key: 'storageLocation',
            width: 120,
            render: (_, record) => (
                <Input
                    value={record.storageLocation}
                    onChange={(e) => handleUpdateItem(record.id, 'storageLocation', e.target.value)}
                    placeholder="Storage location"
                />
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_, record) => (
                <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDeleteItem(record.id)}>
                    Remove
                </Button>
            )
        }
    ];

    // Low stock columns
    const lowStockColumns = [
        {
            title: 'Product Type',
            dataIndex: 'productType',
            key: 'productType',
            width: 100,
            render: (type) => <Tag color={productTypes[type]?.color || 'default'}>{type?.toUpperCase()}</Tag>
        },
        {
            title: 'Product Name',
            dataIndex: 'productName',
            key: 'productName'
        },
        {
            title: 'Brand',
            dataIndex: 'brand',
            key: 'brand',
            width: 120
        },
        {
            title: 'Current Stock',
            dataIndex: 'currentQuantity',
            key: 'currentQuantity',
            width: 120,
            render: (quantity, record) => `${quantity} ${record.unit}`
        },
        {
            title: 'Minimum Level',
            dataIndex: 'minimumStockLevel',
            key: 'minimumStockLevel',
            width: 120,
            render: (level, record) => `${level} ${record.unit}`
        },
        {
            title: 'Required',
            key: 'required',
            width: 100,
            render: (_, record) => {
                const required = Math.max(record.minimumStockLevel - record.currentQuantity, 0);
                return `${required} ${record.unit}`;
            }
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Button type="link" size="small" onClick={() => handleAddLowStockItem(record)}>
                    Add to Restock
                </Button>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            {/* Breadcrumb */}
            {/* <Breadcrumb style={{ marginBottom: '16px' }}>
                <Breadcrumb.Item>
                    <Link to="/">
                        <HomeOutlined /> Home
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/stock">
                        <ShoppingOutlined /> Stock Management
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Restock</Breadcrumb.Item>
            </Breadcrumb> */}

            {/* Page Header */}
            <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '24px' }}>Restock Inventory</h1>
                            <p style={{ margin: 0, color: '#666' }}>Add new stock to inventory from suppliers</p>
                        </div>
                    </div>
                </Col>
                <Col>
                    <Space>
                        <Button onClick={handleGoBack}>Cancel</Button>

                        <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
                            Complete Restock
                        </Button>
                        <Link to="/inventory">
                            <Button icon={<ArrowLeftOutlined />}>Back to Inventory</Button>
                        </Link>
                    </Space>
                </Col>
            </Row>

            <Row gutter={[16, 16]}>
                {/* Quick Actions & Low Stock */}
                <Col xs={24} lg={8}>
                    <Card title="Quick Actions" style={{ marginBottom: '16px' }}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem} block>
                                Add Custom Item
                            </Button>

                            <Button icon={<ReloadOutlined />} onClick={() => setQuickAddModalVisible(true)} block>
                                Quick Add Common Items
                            </Button>

                            {lowStockItems.length > 0 && (
                                <Button type="dashed" onClick={handleAddAllLowStock} block>
                                    Add All Low Stock Items ({lowStockItems.length})
                                </Button>
                            )}
                        </Space>
                    </Card>

                    {/* Low Stock Alert */}
                    {lowStockItems.length > 0 && (
                        <Card
                            title={
                                <Space>
                                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                                    <span>Low Stock Items ({lowStockItems.length})</span>
                                </Space>
                            }
                        >
                            <Table
                                dataSource={lowStockItems}
                                columns={lowStockColumns}
                                pagination={false}
                                size="small"
                                scroll={{ y: 300 }}
                                rowKey="id"
                            />
                        </Card>
                    )}

                    {/* Transaction Information */}
                    <Card title="Transaction Information" style={{ marginTop: '16px' }}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSaveRestock}
                            initialValues={{
                                transactionDate: moment(),
                                paymentStatus: 'paid'
                            }}
                        >
                            <Form.Item
                                label="Restock Date"
                                name="transactionDate"
                                rules={[{ required: true, message: 'Please select restock date' }]}
                            >
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="Select date" />
                            </Form.Item>

                            <Form.Item
                                label="Supplier ID"
                                name="stakeholderId"
                                rules={[{ required: true, message: 'Please enter supplier ID' }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={1}
                                    placeholder="Enter supplier ID"
                                    onChange={handleStakeholderChange}
                                />
                            </Form.Item>

                            {stakeholderInfo && (
                                <Alert
                                    message={stakeholderInfo.name}
                                    description={
                                        <div>
                                            <div>Type: {stakeholderInfo.type}</div>
                                            <div>Contact: {stakeholderInfo.contact}</div>
                                            <div>Address: {stakeholderInfo.address}</div>
                                        </div>
                                    }
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: '16px' }}
                                />
                            )}

                            <Form.Item label="Reference Number" name="referenceNumber">
                                <Input placeholder="Optional reference number" />
                            </Form.Item>

                            <Form.Item label="Delivery Person ID" name="deliveryPersonId">
                                <InputNumber style={{ width: '100%' }} min={1} placeholder="Delivery person ID" />
                            </Form.Item>
                        </Form>
                    </Card>

                    {/* Payment Information */}
                    <Card title="Payment Information" style={{ marginTop: '16px' }}>
                        <Form.Item label="Payment Method" name="paymentMethod">
                            <Select placeholder="Select payment method">
                                {paymentMethods.map((method) => (
                                    <Option key={method.value} value={method.value}>
                                        {method.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item label="Payment Status" name="paymentStatus">
                            <Select placeholder="Select payment status">
                                <Option value="paid">Paid</Option>
                                <Option value="unpaid">Unpaid</Option>
                                <Option value="partial">Partial</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item label="Total Amount (KES)" name="totalAmount">
                            <InputNumber
                                style={{ width: '100%' }}
                                value={totalAmount}
                                formatter={(value) => `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(value) => value.replace(/KES\s?|(,*)/g, '')}
                                disabled
                            />
                        </Form.Item>

                        <Statistic title="Items Count" value={items.length} valueStyle={{ color: '#1890ff' }} />
                    </Card>
                </Col>

                {/* Items Section */}
                <Col xs={24} lg={16}>
                    <Card
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Restock Items ({items.length})</span>
                                <Space>
                                    <Statistic
                                        title="Total Amount"
                                        value={totalAmount}
                                        precision={2}
                                        valueStyle={{ color: '#3f8600', fontSize: '18px' }}
                                        prefix="KES"
                                    />
                                </Space>
                            </div>
                        }
                    >
                        {items.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                <CalculatorOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
                                <p>No items added for restocking. Use quick actions to add items.</p>
                                <Space>
                                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddItem}>
                                        Add Custom Item
                                    </Button>
                                    <Button icon={<ReloadOutlined />} onClick={() => setQuickAddModalVisible(true)}>
                                        Quick Add Common Items
                                    </Button>
                                </Space>
                            </div>
                        ) : (
                            <ProTable
                                dataSource={items}
                                columns={columns}
                                rowKey="id"
                                pagination={false}
                                search={false}
                                options={false}
                                size="small"
                                scroll={{ x: 1500 }}
                                style={{ marginTop: '16px' }}
                            />
                        )}

                        {/* Additional Item Details */}
                        {items.length > 0 && (
                            <div style={{ marginTop: '16px' }}>
                                <Divider />
                                <h4>Additional Item Details</h4>
                                <Row gutter={[16, 16]}>
                                    {items.map((item, index) => (
                                        <Col xs={24} key={item.id}>
                                            <Card size="small" title={`Item ${index + 1}: ${item.productName || 'Unnamed Product'}`}>
                                                <Row gutter={16}>
                                                    <Col xs={24} sm={8}>
                                                        <Form.Item label="Expiration Date">
                                                            <DatePicker
                                                                value={item.expirationDate ? moment(item.expirationDate) : null}
                                                                onChange={(date) =>
                                                                    handleUpdateItem(
                                                                        item.id,
                                                                        'expirationDate',
                                                                        date ? date.format('YYYY-MM-DD') : null
                                                                    )
                                                                }
                                                                style={{ width: '100%' }}
                                                                format="DD/MM/YYYY"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={8}>
                                                        <Form.Item label="Manufacturing Date">
                                                            <DatePicker
                                                                value={item.manufacturingDate ? moment(item.manufacturingDate) : null}
                                                                onChange={(date) =>
                                                                    handleUpdateItem(
                                                                        item.id,
                                                                        'manufacturingDate',
                                                                        date ? date.format('YYYY-MM-DD') : null
                                                                    )
                                                                }
                                                                style={{ width: '100%' }}
                                                                format="DD/MM/YYYY"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={8}>
                                                        <Form.Item label="Item Notes">
                                                            <Input
                                                                value={item.notes || ''}
                                                                onChange={(e) => handleUpdateItem(item.id, 'notes', e.target.value)}
                                                                placeholder="Item-specific notes"
                                                            />
                                                        </Form.Item>
                                                    </Col>
                                                </Row>
                                            </Card>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                    </Card>

                    {/* Transaction Notes */}
                    <Card title="Additional Information" style={{ marginTop: '16px' }}>
                        <Form.Item label="Restock Notes" name="notes">
                            <TextArea rows={4} placeholder="Additional notes about this restock transaction..." />
                        </Form.Item>
                    </Card>
                </Col>
            </Row>

            {/* Action Buttons at Bottom */}
            <Divider />
            <Row justify="center" style={{ marginTop: '24px' }}>
                <Space>
                    <Button onClick={handleGoBack}>Cancel</Button>
                    <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>
                        Complete Restock
                    </Button>
                </Space>
            </Row>

            {/* Quick Add Modal */}
            <Modal
                title="Quick Add Common Items"
                visible={quickAddModalVisible}
                onCancel={() => setQuickAddModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setQuickAddModalVisible(false)}>
                        Close
                    </Button>
                ]}
                width={800}
            >
                <Tabs defaultActiveKey="feed">
                    {Object.entries(productTypes).map(([key, type]) => (
                        <TabPane
                            key={key}
                            tab={
                                <span>
                                    <Tag color={type.color}>{type.label}</Tag>({type.commonItems.length})
                                </span>
                            }
                        >
                            <List
                                dataSource={type.commonItems}
                                renderItem={(item) => (
                                    <List.Item
                                        actions={[
                                            <Button type="link" onClick={() => handleQuickAdd(item, key)}>
                                                Add to Restock
                                            </Button>
                                        ]}
                                    >
                                        <List.Item.Meta
                                            title={item.name}
                                            description={
                                                <Space>
                                                    <Tag>{item.category}</Tag>
                                                    <Text type="secondary">Unit: {item.unit}</Text>
                                                    <Text type="secondary">Typical Price: KES {item.typicalPrice}</Text>
                                                </Space>
                                            }
                                        />
                                    </List.Item>
                                )}
                            />
                        </TabPane>
                    ))}
                </Tabs>
            </Modal>
        </div>
    );
};

export default RestockPage;
